import { ChangeDetectorRef, Component, NgZone, ViewChild, OnInit } from '@angular/core';
import * as XLSX from 'xlsx';
import { finalize, Subscription } from 'rxjs';
import { CostReportService } from '../services/cost-upload-report.service';

@Component({
  selector: 'app-project-bain-targets-upload',
  templateUrl: './project-bain-targets-upload.component.html',
  styleUrls: ['./project-bain-targets-upload.component.scss'],
  standalone: false
})
export class ProjectBainTargetsUploadComponent implements OnInit {

  fetchReportDataSubscription?: Subscription;
  file: File | null = null;
  selectedFileName = '';
  selectedFileSize = 0;

  isValidating = false;
  isUploading = false;
  isViewing = false;

  errorMessage = '';
  successMessage = '';

  validationSummary = '';
  validationColumns: string[] = [];
  validationRows: Array<Record<string, any>> = [];
  excelJsonData: any[] = [];

  viewColumns: string[] = [];
  viewRows: any[] = [];
  allViewRows: any[] = [];

  lastUpdatedOn = '';

  @ViewChild('projectBainExcelFileInput') projectBainExcelFileInput: any;

  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalPages = 0;
  hasCheckedData = false;

  /** Expected Excel Columns */
  readonly requiredColumns = [
    'Market',
    'ProjectType',
    'TargetPyramid',
    'Offshore',
    'Onsite'
  ];

  constructor(private costReportService: CostReportService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.fetchLastUpdatedTimestamp();
  }

  ngOnDestroy(): void {
    if (this.fetchReportDataSubscription) this.fetchReportDataSubscription.unsubscribe();
  }

  get canUpload(): boolean {
    return !!this.file && !this.isValidating && !this.isUploading;
  }

  get hasRecords(): boolean {
    return this.allViewRows.length > 0;
  }

  get canViewData(): boolean {
    return !this.hasCheckedData || this.hasRecords;
  }

  // ================= FILE SELECTION =================
  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      this.clearSelection();
      return;
    }

    const selected = input.files[0];
    if (!/\.xlsx$/i.test(selected.name)) {
      this.errorMessage = 'Only .xlsx files are supported.';
      return;
    }

    this.clearMessages();
    this.file = selected;
    this.selectedFileName = selected.name;
    this.selectedFileSize = selected.size;
  }

  // ================= EXCEL VALIDATION =================
  uploadExcel(): void {
  if (!this.file) {
    this.errorMessage = 'Please select a file first.';
    return;
  }

  this.clearMessages();
  this.isValidating = true;

  const reader = new FileReader();
  reader.onload = (evt: any) => {
    const binaryData = evt.target.result;
    const workbook = XLSX.read(binaryData, { type: 'binary' });

    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    // ✅ Convert Excel → JSON
    this.excelJsonData = XLSX.utils.sheet_to_json(worksheet, {
      defval: '',
      raw: true
    });

    this.validateExcelJson(this.excelJsonData);
  };

  reader.readAsBinaryString(this.file);
}


  private validateExcelJson(data: any[]): void {
  this.validationColumns = ['Row', 'Field', 'Issue'];
  this.validationRows = [];

  if (!data.length) {
    this.failValidation('Excel file is empty.');
    return;
  }

  const requiredColumns = [
    'Market',
    'ProjectType',
    'TargetPyramid',
    'Offshore',
    'Onsite',
  ];

  const fileColumns = Object.keys(data[0]);

  const missingCols = requiredColumns.filter(
    col => !fileColumns.includes(col)
  );

  if (missingCols.length) {
    this.failValidation(`Missing columns: ${missingCols.join(', ')}`);
    return;
  }

  data.forEach((row, index) => {
    const rowNum = index + 2;

    requiredColumns.forEach(col => {
      if (row[col] === '' || row[col] === null) {
        this.validationRows.push({
          Row: rowNum,
          Field: col,
          Issue: 'Value is required'
        });
      }
    });
  });

  if (this.validationRows.length) {
    this.failValidation('Validation failed. Fix errors and re-upload.');
    return;
  }

  this.isValidating = false;
  this.uploadJsonToBackend();
}

  private uploadJsonToBackend(): void {
  this.isUploading = true;

  this.fetchReportDataSubscription = this.costReportService
  .insertProjectBainTargetsData(this.excelJsonData)
  .pipe(finalize(() => this.ngZone.run(() => { this.isUploading = false; this.cdr.markForCheck(); })))
  .subscribe({
    next: (res: any) => {
      if (res !== null && res?.Message?.Message === "Success") {
            this.isUploading = false;
            this.successMessage = 'Project Bain targets uploaded successfully.';
            this.fetchLastUpdatedTimestamp();
            this.clearSuccessMessageAfterDelay();
            this.excelJsonData = [];
            // Clear only file selection, keep success message
            this.file = null;
            this.selectedFileName = '';
            this.selectedFileSize = 0;
            if (this.projectBainExcelFileInput) {
              this.projectBainExcelFileInput.nativeElement.value = '';
            }
          } else {
            this.errorMessage = res.Message;
            this.isUploading = false;
            this.clearSelection();
          }
    },
    error: () => {
      this.isUploading = false;
      this.errorMessage = 'Failed to upload data.';
      this.clearSelection();
    }
  });
}

  private failValidation(message: string): void {
    this.isValidating = false;
    this.validationSummary = message;
    this.errorMessage = message;
  }

  // ================= VIEW DATA FROM DB =================
  viewUploadedData(): void {
    this.isViewing = true;
    this.viewRows = [];
    this.viewColumns = [];
    this.currentPage = 1;
    const flag = 1;

    this.fetchReportDataSubscription = this.costReportService
    .fetchMatrixData(flag)
    .pipe(finalize(() => this.ngZone.run(() => { this.isUploading = false; this.cdr.markForCheck(); })))
    .subscribe({
        next: data => {
          this.allViewRows = data;
          this.hasCheckedData = true;
          this.viewColumns = this.requiredColumns;
          this.totalPages = Math.ceil(this.allViewRows.length / this.pageSize);
          this.updatePageData();
          this.isViewing = false;
        },
        error: () => {
          this.isViewing = false;
          this.hasCheckedData = true;
          this.errorMessage = 'Failed to fetch uploaded data.';
        }
      });
  }

  private updatePageData(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.viewRows = this.allViewRows.slice(startIndex, endIndex);
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePageData();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePageData();
    }
  }

  clearSelection(): void {
    this.file = null;
    this.selectedFileName = '';
    this.selectedFileSize = 0;
    this.errorMessage = '';
    this.successMessage = '';
    this.validationSummary = '';
    this.validationRows = [];
    this.validationColumns = [];
    // Reset file input element
    if (this.projectBainExcelFileInput) {
      this.projectBainExcelFileInput.nativeElement.value = '';
    }
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.validationSummary = '';
    this.validationRows = [];
    this.validationColumns = [];
  }

  private clearSuccessMessageAfterDelay(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 10000); // 10 seconds
  }

  private fetchLastUpdatedTimestamp(): void {
    this.costReportService.getLastUpdatedOn('project-bain-targets').subscribe({
      next: (response: any) => {
        if (response[0][0]?.lastUpdatedOn) {
          this.lastUpdatedOn = new Date(response[0][0].lastUpdatedOn).toLocaleString();
        }
      },
      error: () => {
        this.lastUpdatedOn = 'N/A';
      }
    });
  }
}